import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { Link as MuiLinkIcon } from '@mui/icons-material';
import RotatingLoader from '../../common/ui/RotatingLoader';
import { useAuth } from '../../session/InternalAuthProvider';
import { theme, useLanguage } from '../../common/GeneralUtils';
import { useGetAccountAuthenticated } from '../../session/swr/useGetAccountAuthenticated';
import { TabsPage } from '../../common/components/TabsPage';
import { getPassOrderPage } from '../../utils/AccountUtils';
import { PassType } from '../../api';
import { PassInformationSection } from './sections/PassInformationSection';
import { PassPricingSection } from './sections/PassPricingSection';
import { PassSettingsSection } from './sections/PassSettingsSection';
import { PassDeletionSection } from './sections/PassDeletionSection';
import CopyToClipboardButton from '../../calendar/dialogs/CopyToClipboardButton';
import { SaveHeaderProvider, useSaveHeader } from '../../experienceSettings/saveHeader/SaveHeaderContext';
import { HeaderSaveButton } from '../../experienceSettings/saveHeader/HeaderSaveButton';

import { PassSettingsPageTabs } from './PassSettingsPageTabs';
import { PassTypeChip } from './PassTypeChip';

export const PassOrderPageSettingsPage = () => {
  const language = useLanguage();
  const { account, updateLocalAccount } = useGetAccountAuthenticated({});
  const { passOrderPageId } = useParams();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { authState } = useAuth();

  const passOrderPage = getPassOrderPage(account, passOrderPageId || '');
  const bookingPageLink = `${import.meta.env.VITE_REACT_APP_APP_DOMAIN}/pass/${account?.id}/`;

  if (!authState.isInitialized || !account || !passOrderPage) {
    return (
      <Grid
        container
        sx={{
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <RotatingLoader />
      </Grid>
    );
  }

  const tabContent: Partial<Record<PassSettingsPageTabs, React.ReactElement>> = {
    [PassSettingsPageTabs.INFORMATION]: (
      <PassInformationSection
        passOrderPage={passOrderPage}
        languages={account.customer_facing_widgets_languages}
        updateLocalAccount={updateLocalAccount}
      />
    ),
    [PassSettingsPageTabs.PRICING]: (
      <PassPricingSection passOrderPage={passOrderPage} updateLocalAccount={updateLocalAccount} />
    ),
    [PassSettingsPageTabs.SETTINGS]: (
      <PassSettingsSection passOrderPage={passOrderPage} account={account} updateLocalAccount={updateLocalAccount} />
    ),
    [PassSettingsPageTabs.DELETION]: (
      <PassDeletionSection passOrderPage={passOrderPage} updateLocalAccount={updateLocalAccount} account={account} />
    ),
  };

  return (
    <SaveHeaderProvider tabs={Object.keys(tabContent)}>
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <PassOrderPageSettingsLayout
        isSmallScreen={isSmallScreen}
        title={passOrderPage.label[language] || ''}
        passType={passOrderPage.pass_type}
        bookingHref={bookingPageLink + passOrderPage.id}
        bookingLabel={t('passes.booking_page')}
        orderPageId={passOrderPage.id}
        tabContent={tabContent as Record<string, React.ReactNode>}
      />
    </SaveHeaderProvider>
  );
};

type LayoutProps = {
  isSmallScreen: boolean;
  title: string;
  passType?: PassType;
  bookingHref: string;
  bookingLabel: string;
  orderPageId: string;
  tabContent: Record<string, React.ReactNode>;
};

const PassOrderPageSettingsLayout = ({
  isSmallScreen,
  title,
  passType,
  bookingHref,
  bookingLabel,
  orderPageId,
  tabContent,
}: LayoutProps) => {
  const muiTheme = useTheme();
  const { activeTab, setActiveTab, requestTabChange } = useSaveHeader();
  return (
    <Grid container height="100%" overflow="hidden" flexDirection="column" flexWrap="nowrap" sx={{ gap: 2 }}>
      <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flexShrink: 0 }}>
        <Grid container gap={1} alignItems="flex-start" justifyContent="space-between" flexWrap="nowrap">
          <Grid item xs sx={{ minWidth: 0 }}>
            <Grid container gap={1} alignItems="center">
              <Typography
                variant={isSmallScreen ? 'h6' : 'h4'}
                fontWeight={700}
                sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                {title}
              </Typography>
              <PassTypeChip passType={passType} />
            </Grid>
            <Grid container alignItems="center" gap={0.5}>
              <Link className="no-style-link" to={bookingHref} target="_blank">
                <Typography variant="body2" color="text.secondary">
                  {bookingLabel}
                </Typography>
              </Link>
              <CopyToClipboardButton
                orderPageId={orderPageId}
                text={bookingHref}
                IconComponent={MuiLinkIcon}
                color={muiTheme.palette.text.secondary}
              />
            </Grid>
          </Grid>
          <Grid item>
            <HeaderSaveButton />
          </Grid>
        </Grid>
      </Grid>
      <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <TabsPage
          tabContent={tabContent}
          activeTab={activeTab}
          onActiveTabChange={setActiveTab}
          guardTabChange={requestTabChange}
        />
      </Grid>
    </Grid>
  );
};
